import { useState, useEffect, useCallback, useRef } from 'react';
import { notificationAPI, rankingAPI } from '../services/api';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

/**
 * 실시간 알림 훅 (SSE + 폴링 하이브리드)
 *
 * - SSE로 실시간 이벤트 수신 (후원, 큰손 입장/퇴장)
 * - 폴링은 히스토리/활성큰손 조회용 (30초)
 * - 브라우저 알림 중복 방지 (lastNotifiedId 추적)
 */
export default function useNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [realtimeDonations, setRealtimeDonations] = useState([]);
  const [activeSpenders, setActiveSpenders] = useState([]);
  const [connected, setConnected] = useState(false);
  const lastNotifiedDonationRef = useRef(null);
  const lastNotifiedEventRef = useRef(null);
  const eventSourceRef = useRef(null);
  const clearedRef = useRef(false);  // 초기화 후 폴링 재로드 방지

  // ─── SSE 실시간 연결 ──────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    let reconnectTimer = null;
    let unmounted = false;

    const connectSSE = () => {
      if (unmounted) return;

      const es = new EventSource(`${API_BASE}/notifications/stream?token=${token}`);
      eventSourceRef.current = es;

      es.onopen = () => setConnected(true);

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'connected') {
            setConnected(true);
            return;
          }

          if (data.type === 'donation') {
            clearedRef.current = false;
            setRealtimeDonations(prev => [{
              nickname: data.nickname,
              amount: data.amount,
              message: data.message,
              platform: data.platform,
              tier: data.tier,
              totalDonated: data.totalDonated || 0,
              todayDonated: data.todayDonated || 0,
              donationCount: data.donationCount || 0,
              nativeTotal: data.nativeTotal || null,
              nativeTodayDonated: data.nativeTodayDonated || null,
              timestamp: data.timestamp,
            }, ...prev].slice(0, 50));
          }

          if (data.type === 'big_spender_enter' || data.type === 'big_spender_exit') {
            clearedRef.current = false;
            setNotifications(prev => [{
              event_type: data.type,
              viewer_nickname: data.nickname,
              viewer_tier: data.tier,
              details: data.details,
              created_at: data.timestamp,
            }, ...prev].slice(0, 50));
          }
        } catch (err) {
          console.error('[SSE] Parse error:', err);
        }
      };

      es.onerror = () => {
        setConnected(false);
        es.close();
        if (!unmounted) {
          reconnectTimer = setTimeout(connectSSE, 5000);
        }
      };
    };

    connectSSE();

    return () => {
      unmounted = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  // ─── 히스토리 + 활성큰손 폴링 (30초, 초기 로드용) ─────────
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const [notiRes, spenderRes, donationRes] = await Promise.all([
          notificationAPI.recent({ limit: 20 }),
          notificationAPI.activeSpenders(),
          rankingAPI.recentDonations({ limit: 30 }),
        ]);
        // 초기화 후에는 폴링으로 다시 채우지 않음
        if (!clearedRef.current) {
          setNotifications(prev => prev.length === 0 ? (notiRes.data.notifications || []) : prev);
          setRealtimeDonations(prev => prev.length === 0 ? (donationRes.data.donations || []) : prev);
        }
        setActiveSpenders(spenderRes.data.activeSpenders || []);
      } catch (err) {
        console.error('[Notifications] Fetch error:', err);
      }
    };

    fetchHistory();
    const interval = setInterval(fetchHistory, 30000);
    return () => clearInterval(interval);
  }, []);

  // 브라우저 알림 권한 요청
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // 새 후원 알림 (중복 방지)
  useEffect(() => {
    if (realtimeDonations.length === 0) return;
    const latest = realtimeDonations[0];

    const key = `${latest.nickname}_${latest.amount}_${latest.timestamp}`;
    if (lastNotifiedDonationRef.current === key) return;

    const donatedAt = new Date(latest.timestamp);
    const now = new Date();
    if (now - donatedAt > 10000) return;

    if (!latest.tier || latest.tier === 'normal') return;

    // 데스크탑 앱에서는 항상-위 네이티브 알림창 사용
    if (window.keunson?.notify) {
      lastNotifiedDonationRef.current = key;
      window.keunson.notify({
        kind: 'donation',
        nickname: latest.nickname,
        amount: latest.amount,
        tier: latest.tier,
        platform: latest.platform,
        totalDonated: latest.nativeTotal ?? latest.totalDonated,
      });
      return;
    }

    if ('Notification' in window && Notification.permission === 'granted') {
      lastNotifiedDonationRef.current = key;
      const tierLabel = latest.tier === 'whale' ? '큰손' : latest.tier === 'potential' ? '잠재큰손' : '단골';
      const platformLabel = latest.platform === 'toonation' ? '투네이션' : latest.platform === 'funnation' ? '펀네이션' : '유튜브';
      new Notification(`${tierLabel} 후원!`, {
        body: `${latest.nickname}님이 ${platformLabel}에서 ${latest.amount.toLocaleString()}원 후원!`,
        icon: '/favicon.ico',
      });
    }
  }, [realtimeDonations]);

  // 큰손 입장 알림 (중복 방지)
  useEffect(() => {
    if (notifications.length === 0) return;
    const latest = notifications[0];

    const key = `${latest.viewer_nickname}_${latest.event_type}_${latest.created_at}`;
    if (lastNotifiedEventRef.current === key) return;

    if (latest.event_type !== 'big_spender_enter') return;

    // 데스크탑 앱에서는 항상-위 네이티브 알림창 사용
    if (window.keunson?.notify) {
      lastNotifiedEventRef.current = key;
      window.keunson.notify({
        kind: 'enter',
        nickname: latest.viewer_nickname,
        tier: latest.viewer_tier,
        totalDonated: latest.details?.totalDonated,
      });
      return;
    }

    if ('Notification' in window && Notification.permission === 'granted') {
      lastNotifiedEventRef.current = key;
      const tierLabel = latest.viewer_tier === 'whale' ? '큰손' : '잠재큰손';
      new Notification(`${tierLabel} 입장!`, {
        body: `${latest.viewer_nickname}님이 방송에 입장했습니다.`,
        icon: '/favicon.ico',
      });
    }
  }, [notifications]);

  const clearNotifications = useCallback(() => {
    clearedRef.current = true;  // 폴링이 다시 채우지 않도록 플래그 설정
    setNotifications([]);
    setRealtimeDonations([]);
    lastNotifiedDonationRef.current = null;
    lastNotifiedEventRef.current = null;
  }, []);

  return {
    notifications,
    realtimeDonations,
    activeSpenders,
    connected,
    clearNotifications,
  };
}
